import {
  Box,
  Chip,
  useTheme,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import { tokens } from "../../../../theme";
import AnimatedTable, { ColumnDef } from "../AnimatedTable";
import { useState, useEffect, useCallback } from "react";
import apiService from "../../../../service/Api";
import { useParams } from "react-router-dom";
import ReturnGearDialog, { ReturnFormData } from "../ReturnGear";

export interface EquipmentRental {
  id: number;
  contact_name?: string;
  contact_last_name?: string;
  client_name?: number | string | null;
  rental_start_date: string;
  rental_end_date: string;
  overdue_return_date?: string | null;
  daily_rate?: string | null;
  income?: string | null;
  status:
    | "active"
    | "completed"
    | "returned"
    | "overdue"
    | "cancelled"
    | string;
  condition_before?: string | null;
  condition_after?: string | null;
  notes?: string | null;
  equipment: number;
}

const ROWS_PER_PAGE = 10;

export default function RentalsTab() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();

  const [rentals, setRentals] = useState<EquipmentRental[]>([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Return dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [selectedRental, setSelectedRental] = useState<EquipmentRental | null>(
    null,
  );

  const fetchRentals = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<{
        count: number;
        results: EquipmentRental[];
      }>(
        `/api/equipments/equipment-rentals/?equipment=${id}&page=${page + 1}&page_size=${ROWS_PER_PAGE}`,
      );
      setRentals(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      console.error(err);
      setRentals([]);
      setError("Failed to load rentals.");
    } finally {
      setLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  const handleReturn = (rental: EquipmentRental) => {
    setSelectedRental(rental);
    setReturnDialogOpen(true);
  };

  const handleReturnSubmit = async (data: ReturnFormData) => {
    if (!selectedRental) return;

    const payload: Record<string, unknown> = {
      status: "returned",
      condition_after: data.conditionAfter,
      notes: data.notes || null,
      overdue_return_date: data.isOverdue ? data.returnDate : null,
    };

    await apiService.patch(
      `/api/equipments/equipment-rentals/${selectedRental.id}/`,
      payload,
    );

    setReturnDialogOpen(false);
    setSelectedRental(null);
    await fetchRentals();
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatMoney = (value?: string | null) => {
    if (!value) return "—";
    return `$${Number(value).toFixed(2)}`;
  };

  const formatLabel = (value?: string | null) => {
    if (!value) return "—";
    return value
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getClientName = (row: EquipmentRental) => {
    const contactName = [row.contact_name, row.contact_last_name]
      .filter(Boolean)
      .join(" ");
    return contactName || String(row.client_name || "—");
  };

  const totalPages = Math.ceil(totalCount / ROWS_PER_PAGE) || 1;

  const statusStyles: Record<string, { bg: string; border?: string }> = {
    active: {
      bg: colors.primary[900],
      border: `1px solid ${colors.primary[300]}`,
    },
    completed: { bg: colors.neutral[700] },
    returned: { bg: colors.neutral[700] },
    overdue: { bg: "#FEE2E2", border: "1px solid #EF4444" },
    cancelled: { bg: colors.neutral[700] },
  };

  // Only show Return button for active or overdue rentals
  const canReturn = (status: string) =>
    status === "active" || status === "overdue";

  const columns: ColumnDef<EquipmentRental>[] = [
    {
      key: "client",
      label: "Client",
      cellSx: { fontWeight: 500, color: theme.palette.text.primary },
      render: (row) => getClientName(row),
    },
    {
      key: "rental",
      label: "Rental Period",
      render: (row) =>
        `${formatDate(row.rental_start_date)} - ${formatDate(row.rental_end_date)}`,
    },
    {
      key: "daily_rate",
      label: "Daily Rate",
      render: (row) => formatMoney(row.daily_rate),
    },
    {
      key: "income",
      label: "Income",
      render: (row) => formatMoney(row.income),
    },
    {
      key: "condition",
      label: "Condition",
      render: (row) => formatLabel(row.condition_before),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const statusKey = row.status?.toLowerCase() || "";
        const style = statusStyles[statusKey] ?? { bg: colors.neutral[700] };
        return (
          <Chip
            label={formatLabel(row.status)}
            size="small"
            sx={{
              bgcolor: style.bg,
              border: style.border ?? "none",
              color: theme.palette.text.primary,
              fontWeight: 600,
              borderRadius: "16px",
              fontSize: 12,
            }}
          />
        );
      },
    },
    {
      key: "actions",
      label: "",
      render: (row) =>
        canReturn(row.status) ? (
          <Tooltip title="Return gear">
            <IconButton
              size="small"
              onClick={() => handleReturn(row)}
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: colors.primary[400],
                  background: colors.primary[800],
                },
              }}
            >
              <AssignmentReturnOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        borderRadius: "16px",
        p: 3,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AnimatedTable
          columns={columns}
          rows={rentals}
          getRowId={(row) => row.id}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyMessage="No rentals found."
        />
      )}

      <ReturnGearDialog
        open={returnDialogOpen}
        onClose={() => {
          setReturnDialogOpen(false);
          setSelectedRental(null);
        }}
        onSubmit={handleReturnSubmit}
        rental={selectedRental}
      />
    </Box>
  );
}
